import { Injectable } from '@angular/core';

export interface CanvasCoordinates {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PdfCoordinates {
  x: number;
  y: number;
  width: number;
  height: number;
}

@Injectable({
  providedIn: 'root'
})
export class CoordinateConverterService {

  constructor() { }

  /**
   * Convert canvas coordinates to PDF points
   *
   * pdf.js renders at the scale you provide, so:
   * - Canvas pixels = PDF points * scale
   * - PDF points = Canvas pixels / scale
   *
   * @param canvasCoords Canvas coordinates (in scaled pixels)
   * @param pageHeight Height of the PDF page in points (UNSCALED)
   * @param scale Current scale/zoom factor used for rendering
   * @returns PDF coordinates in points
   */
  canvasToPdf(
    canvasCoords: CanvasCoordinates,
    pageHeight: number,
    scale: number = 1.0
  ): PdfCoordinates {
    // Convert scaled canvas pixels to PDF points
    // Canvas is rendered at 'scale', so divide by scale to get PDF points
    const pdfX = canvasCoords.x / scale;
    const pdfWidth = canvasCoords.width / scale;
    const pdfHeight = canvasCoords.height / scale;

    // PDF Y-axis is inverted (0 is at bottom, increases upward)
    // Canvas Y-axis is standard (0 is at top, increases downward)
    // canvasY is from top, so: pdfY = pageHeight - (canvasY/scale) - pdfHeight
    const canvasYInPdfUnits = canvasCoords.y / scale;
    const pdfY = pageHeight - canvasYInPdfUnits - pdfHeight;

    return {
      x: pdfX,
      y: pdfY,
      width: pdfWidth,
      height: pdfHeight
    };
  }

  /**
   * Convert PDF points to canvas coordinates
   *
   * @param pdfCoords PDF coordinates in points
   * @param pageHeight Height of the PDF page in points (UNSCALED)
   * @param scale Current scale/zoom factor used for rendering
   * @returns Canvas coordinates in scaled pixels
   */
  pdfToCanvas(
    pdfCoords: PdfCoordinates,
    pageHeight: number,
    scale: number = 1.0
  ): CanvasCoordinates {
    // Convert PDF points to scaled canvas pixels
    const canvasX = pdfCoords.x * scale;
    const canvasWidth = pdfCoords.width * scale;
    const canvasHeight = pdfCoords.height * scale;

    // Invert Y-axis for canvas coordinate system
    // pdfY is from bottom, canvas Y is from top
    const canvasY = (pageHeight - pdfCoords.y - pdfCoords.height) * scale;

    return {
      x: canvasX,
      y: canvasY,
      width: canvasWidth,
      height: canvasHeight
    };
  }

  /**
   * Convert PDF annotation rect to canvas coordinates
   * PDF annotation rect format: [x1, y1, x2, y2] where (x1,y1) is bottom-left
   * @param rect PDF annotation rect array
   * @param pageHeight Height of the PDF page
   * @param scale Current scale/zoom factor
   * @returns Canvas coordinates
   */
  pdfRectToCanvas(
    rect: number[],
    pageHeight: number,
    scale: number = 1.0
  ): CanvasCoordinates {
    if (rect.length !== 4) {
      throw new Error('Invalid rect array. Expected 4 values.');
    }

    const [x1, y1, x2, y2] = rect;

    // Convert to standard coordinates (x, y, width, height)
    const pdfCoords: PdfCoordinates = {
      x: Math.min(x1, x2),
      y: Math.min(y1, y2),
      width: Math.abs(x2 - x1),
      height: Math.abs(y2 - y1)
    };

    return this.pdfToCanvas(pdfCoords, pageHeight, scale);
  }

  /**
   * Convert canvas coordinates to PDF annotation rect format
   * @param canvasCoords Canvas coordinates
   * @param pageHeight Height of the PDF page
   * @param scale Current scale/zoom factor
   * @returns PDF annotation rect array [x1, y1, x2, y2]
   */
  canvasToPdfRect(
    canvasCoords: CanvasCoordinates,
    pageHeight: number,
    scale: number = 1.0
  ): number[] {
    const pdfCoords = this.canvasToPdf(canvasCoords, pageHeight, scale);

    // Convert to rect format [x1, y1, x2, y2]
    return [
      pdfCoords.x,
      pdfCoords.y,
      pdfCoords.x + pdfCoords.width,
      pdfCoords.y + pdfCoords.height
    ];
  }

  /**
   * Adjust coordinates for canvas offset/padding
   * @param coords Canvas coordinates
   * @param offsetX X offset
   * @param offsetY Y offset
   * @returns Adjusted canvas coordinates
   */
  adjustForOffset(
    coords: CanvasCoordinates,
    offsetX: number,
    offsetY: number
  ): CanvasCoordinates {
    return {
      x: coords.x - offsetX,
      y: coords.y - offsetY,
      width: coords.width,
      height: coords.height
    };
  }

  /**
   * Get mouse position relative to canvas
   * @param event Mouse event
   * @param canvas Canvas element
   * @returns Canvas coordinates of mouse position
   */
  getMousePosition(event: MouseEvent, canvas: HTMLCanvasElement): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  }

  /**
   * Check if two rectangles overlap
   * @param rect1 First rectangle
   * @param rect2 Second rectangle
   * @returns True if rectangles overlap
   */
  rectanglesOverlap(
    rect1: CanvasCoordinates | PdfCoordinates,
    rect2: CanvasCoordinates | PdfCoordinates
  ): boolean {
    return !(
      rect1.x + rect1.width < rect2.x ||
      rect2.x + rect2.width < rect1.x ||
      rect1.y + rect1.height < rect2.y ||
      rect2.y + rect2.height < rect1.y
    );
  }

  /**
   * Calculate the area of overlap between two rectangles
   * @param rect1 First rectangle
   * @param rect2 Second rectangle
   * @returns Area of overlap in square units
   */
  calculateOverlapArea(
    rect1: CanvasCoordinates | PdfCoordinates,
    rect2: CanvasCoordinates | PdfCoordinates
  ): number {
    if (!this.rectanglesOverlap(rect1, rect2)) {
      return 0;
    }

    const overlapX = Math.max(0, Math.min(rect1.x + rect1.width, rect2.x + rect2.width) - Math.max(rect1.x, rect2.x));
    const overlapY = Math.max(0, Math.min(rect1.y + rect1.height, rect2.y + rect2.height) - Math.max(rect1.y, rect2.y));

    return overlapX * overlapY;
  }

  /**
   * Normalize rectangle coordinates (ensure positive width/height)
   * @param coords Coordinates that might have negative dimensions
   * @returns Normalized coordinates
   */
  normalizeRectangle(coords: CanvasCoordinates): CanvasCoordinates {
    return {
      x: coords.width < 0 ? coords.x + coords.width : coords.x,
      y: coords.height < 0 ? coords.y + coords.height : coords.y,
      width: Math.abs(coords.width),
      height: Math.abs(coords.height)
    };
  }
}
